<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Support Tickets</h1>
    <p class="text-muted mb-0">Create and track your support requests</p>
  </div>
  <div class="d-flex flex-column align-items-end gap-2">
    <button class="btn btn-primary" (click)="createTicket()">
      <i class="bi bi-plus-lg me-2"></i>New Ticket
    </button>
    <div class="d-flex align-items-center gap-2 small">
      <i class="bi bi-telephone text-muted"></i>
      <span class="text-muted">Technical Support:</span>
      <a [href]="'tel:' + technicalSupportPhone" class="fw-medium">
        {{ formattedSupportPhone }}
      </a>
    </div>
  </div>
</div>

<!-- Stats Summary -->
<div class="row g-3 mb-4">
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle">
            <i class="bi bi-envelope-open text-primary"></i>
          </div>
          <div>
            <div class="stat-value">{{ openCount }}</div>
            <div class="stat-label text-muted">Open Tickets</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-warning-subtle">
            <i class="bi bi-hourglass-split text-warning"></i>
          </div>
          <div>
            <div class="stat-value">{{ inProgressCount }}</div>
            <div class="stat-label text-muted">In Progress</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle">
            <i class="bi bi-check-circle text-success"></i>
          </div>
          <div>
            <div class="stat-value">{{ resolvedCount }}</div>
            <div class="stat-label text-muted">Resolved</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Tickets List -->
<div class="card">
  <div class="card-header p-3">
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Your Tickets</h5>
      <div class="d-flex gap-2">
        <!-- Search Input -->
        <div class="input-group input-group-sm w-220px">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input
            type="text"
            class="form-control"
            placeholder="Search tickets..."
            [(ngModel)]="filters.search"
            (ngModelChange)="applyFilters()"
          >
        </div>
        <button
          class="btn btn-sm"
          [class.btn-primary]="isFilterOpen || activeFilterCount > 0"
          [class.btn-outline-secondary]="!isFilterOpen && activeFilterCount === 0"
          (click)="toggleFilter()"
        >
          <i class="bi bi-funnel me-1"></i>Filter
          <span *ngIf="activeFilterCount > 0" class="badge bg-white text-primary ms-1">{{ activeFilterCount }}</span>
        </button>
      </div>
    </div>

    <!-- Filter Panel -->
    <div class="filter-panel" *ngIf="isFilterOpen">
      <hr class="my-3">
      <div class="row g-3">
        <!-- Status Filter -->
        <div class="col-md-3">
          <label class="form-label small">Status</label>
          <select class="form-select form-select-sm" [(ngModel)]="filters.status" (ngModelChange)="applyFilters()">
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <!-- Priority Filter -->
        <div class="col-md-3">
          <label class="form-label small">Priority</label>
          <select class="form-select form-select-sm" [(ngModel)]="filters.priority" (ngModelChange)="applyFilters()">
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <!-- Category Filter -->
        <div class="col-md-3">
          <label class="form-label small">Category</label>
          <select class="form-select form-select-sm" [(ngModel)]="filters.category" (ngModelChange)="applyFilters()">
            <option value="">All Categories</option>
            <option *ngFor="let cat of uniqueCategories" [value]="cat">{{ cat }}</option>
          </select>
        </div>

        <!-- Date Range -->
        <div class="col-md-3">
          <label class="form-label small">Date Range</label>
          <div class="d-flex gap-2">
            <input
              type="date"
              class="form-control form-control-sm"
              [(ngModel)]="filters.dateFrom"
              (ngModelChange)="applyFilters()"
            >
            <input
              type="date"
              class="form-control form-control-sm"
              [(ngModel)]="filters.dateTo"
              (ngModelChange)="applyFilters()"
            >
          </div>
        </div>
      </div>

      <!-- Clear Filters -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <span class="text-muted small">
          Showing {{ filteredTickets.length }} of {{ totalTickets }} tickets
        </span>
        <button
          *ngIf="activeFilterCount > 0"
          class="btn btn-sm btn-link text-decoration-none"
          (click)="clearFilters()"
        >
          <i class="bi bi-x-lg me-1"></i>Clear Filters
        </button>
      </div>
    </div>
  </div>

  <div class="card-body p-0">
    <ng-container *ngIf="!isLoading && filteredTickets.length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th class="ps-3">Ticket</th>
              <th>Subject</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Created</th>
              <th class="text-end pe-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let ticket of filteredTickets" class="record-row cursor-pointer" (click)="viewTicket(ticket)">
              <td class="ps-3">
                <span class="fw-medium text-primary">{{ ticket.ticketNumber }}</span>
              </td>
              <td>
                <div class="d-flex flex-column">
                  <span class="fw-medium text-truncate mw-300px">{{ ticket.subject }}</span>
                  <span class="text-muted small" *ngIf="ticket.droneName">
                    <i class="icon-drone me-1"></i>{{ ticket.droneName }}
                  </span>
                </div>
              </td>
              <td>
                <span class="text-muted">{{ ticket.category }}</span>
              </td>
              <td>
                <span class="badge" [ngClass]="getPriorityBadgeClass(ticket.priority)">
                  {{ getPriorityText(ticket.priority) }}
                </span>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusBadgeClass(ticket.status)">
                  {{ getStatusText(ticket.status) }}
                </span>
              </td>
              <td>
                <span class="text-muted small">{{ formatDate(ticket.createdAt) }}</span>
              </td>
              <td class="text-end pe-3" (click)="$event.stopPropagation()">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewTicket(ticket)" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button
                    class="btn btn-outline-secondary text-warning"
                    (click)="withdrawTicket(ticket)"
                    title="Withdraw ticket"
                    *ngIf="ticket.status === 'open'"
                  >
                    <i class="bi bi-x-circle"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <ng-container *ngIf="tickets.length === 0; else noResults">
          <div class="empty-state-icon mb-3">
            <i class="bi bi-ticket-perforated"></i>
          </div>
          <h5>No Support Tickets Yet</h5>
          <p class="text-muted mb-3">Create your first support ticket to get help from our team.</p>
          <button class="btn btn-primary" (click)="createTicket()">
            <i class="bi bi-plus-lg me-2"></i>Create First Ticket
          </button>
        </ng-container>
        <ng-template #noResults>
          <div class="empty-state-icon mb-3">
            <i class="bi bi-funnel"></i>
          </div>
          <h5>No Tickets Match Filters</h5>
          <p class="text-muted mb-3">Try adjusting your filters to see more results.</p>
          <button class="btn btn-outline-primary" (click)="clearFilters()">
            <i class="bi bi-x-lg me-2"></i>Clear Filters
          </button>
        </ng-template>
      </div>
    </ng-template>
  </div>
</div>

<!-- Withdraw Ticket Modal -->
<app-withdraw-ticket-modal
  #withdrawModal
  (confirmed)="onWithdrawConfirmed($event)"
  (cancelled)="onWithdrawCancelled()"
></app-withdraw-ticket-modal>
